/**
 * Shared tile-layer definitions for the committee/admin course + mark maps
 * (ra-9b8b.1).
 *
 * We render a real nautical chart by stacking two tile layers:
 *
 *   1. BASE  — standard OpenStreetMap raster (land vs. sea, coastline, place
 *              names). This is the opaque bottom layer.
 *   2. SEAMARK — the OpenSeaMap "seamark" layer. These are TRANSPARENT PNG
 *              tiles carrying only the nautical marks (buoyage, lights, depth
 *              contours, harbour detail), so they must be layered ON TOP of an
 *              opaque base map — OpenSeaMap does not ship a standalone basemap.
 *
 * Each export is a plain props object; spread it onto a react-leaflet
 * <TileLayer {...BASE_TILE_LAYER} />. Leaflet auto-merges the per-layer
 * `attribution` strings into a single attribution control, so both credits show.
 *
 * Proving the tile source, attribution, rendering and performance here (web,
 * fast iteration) precedes the follow-on app port.
 */

/** Opaque OpenStreetMap raster base — land, sea and coastline. */
export const BASE_TILE_LAYER = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  maxZoom: 19,
};

/**
 * Transparent OpenSeaMap seamark overlay — buoyage, lights, harbour detail.
 * Must be rendered AFTER the base layer so it draws on top of it.
 */
export const SEAMARK_TILE_LAYER = {
  url: 'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
  attribution:
    'Sea marks &copy; <a href="https://www.openseamap.org">OpenSeaMap</a> contributors',
  // OpenSeaMap seamark tiles only exist up to z18 (the server 404s beyond).
  // Keep maxZoom aligned with the base layer (19) so the overlay stays
  // visible at the map's deepest zoom, and let maxNativeZoom up-scale the
  // z18 tiles for z19 instead of dropping the layer entirely.
  maxZoom: 19,
  maxNativeZoom: 18,
  // Transparent tiles — declaring it lets Leaflet skip the opaque-tile
  // optimisations and keeps the base map visible underneath.
  transparent: true,
};
